import { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from 'arbitrip-common/translation/const';
import { makeStyles } from '@mui/styles';

import { ReactComponent as UsaFlag } from '@/img/icons/usa.svg';
import { ReactComponent as IsraelFlag } from '@/img/icons/israel.svg';

import usaFlagUrl from '@/img/icons/usa.svg';
import israelFlagUrl from '@/img/icons/israel.svg';
import worldIconUrl from '@/img/icons/world.svg';

const useStyles = makeStyles((theme) => ({
	button: {
		marginInlineEnd: 4,
		minWidth: 0,
		padding: 0,
		width: 26,
		height: 16,
		borderRadius: 2,
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		'&:hover': {
			backgroundColor: 'transparent',
		},
	},
	flag: {
		marginInlineStart: 8,
	},
	menu: {
		width: 180,
		borderRadius: 12,
		padding: 16,
	},
	menuList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		padding: 0,
	},
	listItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		borderRadius: 8,
		border: '1px solid #E5E7EB',
		height: 56,
		padding: theme.spacing(2),
	},
	listItemSelected: {
		border: `1px solid ${theme.palette.blue.main}`,
	},
	listItemText: {
		fontSize: 18,
		flexGrow: 1,
		marginInlineStart: 8,
	},
}));

const languageOptions = [
	{ code: SUPPORTED_LANGUAGES.EN, fullLabel: 'English', flag: UsaFlag, flagUrl: usaFlagUrl },
	{ code: SUPPORTED_LANGUAGES.HE, fullLabel: 'עברית', flag: IsraelFlag, flagUrl: israelFlagUrl },
];

const LanguageSwitcher = ({ changeLanguage, updateLanguageOnDB }) => {
	const classes = useStyles();
	const { i18n } = useTranslation();
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const currentLang = languageOptions.find((lang) => lang.code === i18n.language);
	const currentFlagUrl = currentLang?.flagUrl || worldIconUrl;

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (langCode) => {
		setAnchorEl(null);
		if (langCode && langCode !== i18n.language) {
			changeLanguage(langCode);
			updateLanguageOnDB(langCode);

			if (window.zE) {
				window.zE('messenger:set', 'locale', langCode);
			}
		}
	};

	return (
		<>
			<Button
				aria-controls="language-menu"
				aria-haspopup="true"
				onClick={handleClick}
				className={classes.button}
				style={{ backgroundImage: `url(${currentFlagUrl})` }}
				disableRipple
			/>

			<Menu
				id="language-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={() => handleClose(null)}
				MenuListProps={{ className: classes.menuList }}
				PaperProps={{ className: classes.menu }}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				{languageOptions.map((lang) => (
					<MenuItem
						key={lang.code}
						onClick={() => handleClose(lang.code)}
						className={`${classes.listItem} ${lang.code === i18n.language ? classes.listItemSelected : ''}`}
					>
						<ListItemIcon>
							<lang.flag className={classes.flag} />
						</ListItemIcon>
						<ListItemText className={classes.listItemText} primary={lang.fullLabel} />
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default LanguageSwitcher;
